import { ActiveAdventCalendar_getActiveAdventCalendar_buildYourOwnPromoCode as BuildYourOwnPromoCode } from "../types/__generated__/ActiveAdventCalendar";
import { PRODUCT_TYPE_IDS } from "./constants";

/*
 * Build-your-own advent calendar: a promo code on the member's one-off cart
 * grants one free calendar box for N paid wines (worth at least S, when a spend
 * threshold is set). The backend computes the granted quantity, the progress
 * and whether checkout is blocked on every cart read (`promoCodeFreebies`);
 * the client only counts wines itself for guests, who have no server cart yet.
 *
 * It is one calendar per order: there is no telling afterwards which wines went
 * into which box. The add-to-cart guard stops the wine count going past N, so a
 * cart with more is a leftover from before the rule (or another device) and is
 * shown as wines to remove, never as a second calendar in the making.
 */

/** The PromoCodeFreebieGrant fragment, structurally (the cart and checkout queries both select it). */
export interface PromoCodeFreebieGrantLike {
  quantityPerUnit: number;
  perNumberOfWines: number | null;
  perWineSpend?: number | null;
  grantedQuantity: number;
  winesTowardsNextUnit: number | null;
  winesUntilNextUnit: number | null;
  wineSpendTowardsNextUnit?: number | null;
  wineSpendUntilNextUnit?: number | null;
  isAvailable: boolean;
  /** Checkout needs every started box complete (12 wines and the spend threshold). */
  requiresCompleteUnits?: boolean;
  /** Wines beyond the complete box: too many for a one-calendar order. */
  winesOverLastUnit?: number | null;
  blocksCheckout?: boolean;
  /** The message checkout would return right now; null when it can proceed. */
  checkoutError?: string | null;
  freebie: {
    imageMedium: string | null;
    product: { id?: number | null; name: string; coverPhotoSmallPng?: string | null } | null;
  } | null;
}

export interface BuildYourOwnProgress {
  freebieName: string;
  freebieImage: string | null;
  perNumberOfWines: number;
  perWineSpend: number | null;
  quantityPerUnit: number;
  grantedQuantity: number;
  winesTowardsNextUnit: number;
  winesUntilNextUnit: number;
  wineSpendTowardsNextUnit: number | null;
  wineSpendUntilNextUnit: number | null;
  winesOverLastUnit: number;
  requiresCompleteUnits: boolean;
  blocksCheckout: boolean;
  checkoutError: string | null;
  isAvailable: boolean;
}

/** What a guest chose on the advent page, kept until the code can be applied after login/signup. */
export interface BuildYourOwnIntent {
  promoCode: string;
  perNumberOfWines: number;
  perWineSpend: number | null;
  requiresCompleteUnits: boolean;
  quantityPerUnit: number;
  freebieName: string;
  freebieImage: string | null;
}

interface CartItemLike {
  quantity: number;
  isRedeemedWithPoints?: boolean | null;
  product: {
    name?: string | null;
    sellingPrice?: number | null;
    productType: { id: number | null } | null;
  } | null;
}

const isWineItem = (item: CartItemLike | null | undefined) =>
  item?.product?.productType?.id === PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_WINE;

const isPaidWineItem = (item: CartItemLike | null | undefined) =>
  isWineItem(item) && !item?.isRedeemedWithPoints;

/** A grant scaled per N wines (null `perNumberOfWines` means granted once per order: no progress to show). */
export const isScaledGrant = (grant: PromoCodeFreebieGrantLike | null | undefined): boolean =>
  !!grant && !!grant.perNumberOfWines && grant.perNumberOfWines > 0;

const freebieImageOf = (freebie: PromoCodeFreebieGrantLike["freebie"]) =>
  freebie?.imageMedium ?? freebie?.product?.coverPhotoSmallPng ?? null;

export const toBuildYourOwnProgress = (grant: PromoCodeFreebieGrantLike): BuildYourOwnProgress | null => {
  if (!isScaledGrant(grant)) return null;
  const perNumberOfWines = grant.perNumberOfWines as number;
  const winesTowardsNextUnit = grant.winesTowardsNextUnit ?? 0;
  return {
    freebieName: grant.freebie?.product?.name ?? "",
    freebieImage: freebieImageOf(grant.freebie),
    perNumberOfWines,
    perWineSpend: grant.perWineSpend ?? null,
    quantityPerUnit: grant.quantityPerUnit,
    grantedQuantity: grant.grantedQuantity,
    winesTowardsNextUnit,
    winesUntilNextUnit: grant.winesUntilNextUnit ?? perNumberOfWines,
    wineSpendTowardsNextUnit: grant.wineSpendTowardsNextUnit ?? null,
    wineSpendUntilNextUnit: grant.wineSpendUntilNextUnit ?? null,
    winesOverLastUnit: grant.winesOverLastUnit ?? winesTowardsNextUnit,
    requiresCompleteUnits: grant.requiresCompleteUnits ?? false,
    blocksCheckout: grant.blocksCheckout ?? false,
    checkoutError: grant.checkoutError ?? null,
    isAvailable: grant.isAvailable,
  };
};

/** The first per-N-wines grant on a server cart, which is the build-your-own calendar. */
export const getBuildYourOwnProgress = (
  grants: (PromoCodeFreebieGrantLike | null)[] | null | undefined
): BuildYourOwnProgress | null => {
  const scaled = (grants ?? []).find(isScaledGrant);
  return scaled ? toBuildYourOwnProgress(scaled) : null;
};

/**
 * A complete calendar with wines beyond it. One calendar per order, so those
 * wines are too many rather than the start of a second box; the guard keeps new
 * carts from getting here.
 */
export const hasExtraWines = (progress: BuildYourOwnProgress): boolean =>
  progress.grantedQuantity > 0 && progress.winesOverLastUnit > 0;

/** Paid wine bottles in a cart: local carts have no points redemptions, members' carts flag them. */
export const countCartWineBottles = (items: (CartItemLike | null)[] | null | undefined): number =>
  (items ?? []).reduce((total, item) => (isPaidWineItem(item) ? total + (item?.quantity ?? 0) : total), 0);

/** Value of the paid wine bottles in a cart, for the spend threshold. */
export const countCartWineSpend = (items: (CartItemLike | null)[] | null | undefined): number =>
  (items ?? []).reduce(
    (total, item) =>
      isPaidWineItem(item) ? total + (item?.product?.sellingPrice ?? 0) * (item?.quantity ?? 0) : total,
    0
  );

/**
 * Guest-only progress preview from the local cart. Mirrors
 * `PromoCode.get_freebie_grants` on the backend (the box is earned once both
 * the wine-count and spend thresholds are reached) capped at the one calendar
 * an order can hold; once a server cart exists its `promoCodeFreebies` is the
 * source of truth. The checkout message itself is the backend's to write, so
 * it stays null here. With the complete-box rule, checkout waits for the box
 * to be earned and for any wines beyond it to go.
 */
export const deriveLocalBuildYourOwnProgress = (
  intent: BuildYourOwnIntent,
  wineBottles: number,
  wineSpend = 0
): BuildYourOwnProgress => {
  const wines = Math.max(wineBottles, 0);
  const spend = Math.max(wineSpend, 0);
  const { perNumberOfWines, perWineSpend } = intent;
  const unitsByCount = Math.min(Math.floor(wines / perNumberOfWines), 1);
  const units = perWineSpend ? Math.min(unitsByCount, Math.floor(spend / perWineSpend)) : unitsByCount;
  const winesOverLastUnit = wines - perNumberOfWines * units;
  return {
    freebieName: intent.freebieName,
    freebieImage: intent.freebieImage,
    perNumberOfWines,
    perWineSpend: perWineSpend ?? null,
    quantityPerUnit: intent.quantityPerUnit,
    grantedQuantity: intent.quantityPerUnit * units,
    winesTowardsNextUnit: Math.min(winesOverLastUnit, perNumberOfWines),
    winesUntilNextUnit: Math.max(perNumberOfWines * (units + 1) - wines, 0),
    wineSpendTowardsNextUnit: perWineSpend ? Math.min(spend - perWineSpend * units, perWineSpend) : null,
    wineSpendUntilNextUnit: perWineSpend ? Math.max(perWineSpend * (units + 1) - spend, 0) : null,
    winesOverLastUnit,
    requiresCompleteUnits: intent.requiresCompleteUnits,
    blocksCheckout: intent.requiresCompleteUnits && (units !== 1 || winesOverLastUnit > 0),
    checkoutError: null,
    isAvailable: true,
  };
};

/** Fraction of the way to the calendar, for the progress bar: full once it is earned. */
export const getBuildYourOwnProgressRatio = (progress: BuildYourOwnProgress): number => {
  if (progress.grantedQuantity > 0) return 1;
  return progress.perNumberOfWines > 0 ? progress.winesTowardsNextUnit / progress.perNumberOfWines : 0;
};

/** Names of the cart lines that make the backend set a wines-only promo code aside. */
export const getNonWineCartItemNames = (items: (CartItemLike | null)[] | null | undefined): string[] =>
  (items ?? [])
    .filter((item) => item?.product && !isWineItem(item))
    .map((item) => item?.product?.name ?? "")
    .filter(Boolean);

const isSubscriptionItem = (item: CartItemLike | null | undefined) =>
  item?.product?.productType?.id === PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION;

/** Whether a product may join a build-your-own order, which holds wines and nothing else. */
export const isWineProduct = (
  product: { productType?: { id: number | null } | null } | null | undefined
): boolean => product?.productType?.id === PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_WINE;

/**
 * Cart lines a build-your-own order cannot keep: everything but wines and the
 * Subscription line (which the one-off switch drops by itself).
 */
export const getOtherCartLines = <T extends CartItemLike>(items: (T | null)[] | null | undefined): T[] =>
  (items ?? []).filter(
    (item): item is T => !!item?.product && !isWineItem(item) && !isSubscriptionItem(item)
  );

/* ---------------------------------------------------------------------------
 * Local markers. localStorage isn't reactive, so writes dispatch a same-tab
 * event (like guestBoxIntent) for UI that must follow the choice live.
 * ------------------------------------------------------------------------- */

const INTENT_KEY = "buildYourOwnAdventIntent";
const APPLIED_KEY = "buildYourOwnAdventApplied";
const CHANGE_EVENT = "buildYourOwnAdventChange";

const notifyChange = () => window.dispatchEvent(new Event(CHANGE_EVENT));

/** A guest's pending choice: the code is applied right after login/signup + cart transfer. */
export const getBuildYourOwnIntent = (): BuildYourOwnIntent | null => {
  try {
    const raw = window.localStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BuildYourOwnIntent>;
    if (!parsed.promoCode || !parsed.perNumberOfWines) return null;
    return {
      promoCode: parsed.promoCode,
      perNumberOfWines: parsed.perNumberOfWines,
      perWineSpend: parsed.perWineSpend ?? null,
      requiresCompleteUnits: parsed.requiresCompleteUnits ?? false,
      quantityPerUnit: parsed.quantityPerUnit ?? 1,
      freebieName: parsed.freebieName ?? "",
      freebieImage: parsed.freebieImage ?? null,
    };
  } catch {
    return null;
  }
};

export const setBuildYourOwnIntent = (intent: BuildYourOwnIntent) => {
  window.localStorage.setItem(INTENT_KEY, JSON.stringify(intent));
  notifyChange();
};

export const clearBuildYourOwnIntent = () => {
  window.localStorage.removeItem(INTENT_KEY);
  notifyChange();
};

/** The code a member applied from the advent page, and how many wines its calendar takes. */
export interface BuildYourOwnApplied {
  promoCode: string;
  /** Null for a marker written before the size was kept. */
  perNumberOfWines: number | null;
}

/**
 * The code a member applied from the advent page. The backend drops a wines-only
 * code from the cart (and restores it) as non-wine items come and go, so this is
 * how the cart knows a missing `promoCodeFreebies` means "set aside" rather than
 * "never chosen" — and, with the calendar's size kept alongside, how the add
 * guard keeps the wine cap while the code is set aside or the cart has only
 * been partly read. Tolerates the earlier bare-string marker.
 */
export const getBuildYourOwnApplied = (): BuildYourOwnApplied | null => {
  const raw = window.localStorage.getItem(APPLIED_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<BuildYourOwnApplied>;
    if (parsed && typeof parsed === "object" && parsed.promoCode) {
      return { promoCode: parsed.promoCode, perNumberOfWines: parsed.perNumberOfWines ?? null };
    }
  } catch {
    // A bare code from before the size was kept.
  }
  return { promoCode: raw, perNumberOfWines: null };
};

export const markBuildYourOwnApplied = (promoCode: string, perNumberOfWines: number | null = null) => {
  window.localStorage.setItem(APPLIED_KEY, JSON.stringify({ promoCode, perNumberOfWines }));
  notifyChange();
};

export const clearBuildYourOwnApplied = () => {
  window.localStorage.removeItem(APPLIED_KEY);
  notifyChange();
};

/** Returns an unsubscribe function. */
export const onBuildYourOwnChange = (listener: () => void) => {
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
};

interface SetAsideInput {
  appliedPromoCode: string | null;
  discountCode: string | null | undefined;
  grants: (PromoCodeFreebieGrantLike | null)[] | null | undefined;
  items: (CartItemLike | null)[] | null | undefined;
}

/**
 * Whether a member's build-your-own code has been set aside because the cart
 * holds something other than wine. The backend reapplies it by itself once
 * those items are removed, so the UI only has to explain the situation.
 */
export const getBuildYourOwnSetAside = ({
  appliedPromoCode,
  discountCode,
  grants,
  items,
}: SetAsideInput): { isSetAside: boolean; blockingItemNames: string[] } => {
  const blockingItemNames = getNonWineCartItemNames(items);
  const isSetAside =
    !!appliedPromoCode && !discountCode && !getBuildYourOwnProgress(grants) && blockingItemNames.length > 0;
  return { isSetAside, blockingItemNames: isSetAside ? blockingItemNames : [] };
};

/**
 * The offer an edition's promo code makes, as the intent a guest can store:
 * its scaled freebie row (a box per N wines, optionally with a minimum total).
 */
export const getBuildYourOwnOffer = (
  promoCode: BuildYourOwnPromoCode | null | undefined
): BuildYourOwnIntent | null => {
  if (!promoCode) return null;
  const row = promoCode.promocodefreebieSet.find(
    (freebieRow) =>
      !!freebieRow.perNumberOfWines && freebieRow.perNumberOfWines > 0 && freebieRow.freebie?.product
  );
  if (!row || !row.perNumberOfWines) return null;
  return {
    promoCode: promoCode.name,
    perNumberOfWines: row.perNumberOfWines,
    perWineSpend: row.perWineSpend ?? null,
    requiresCompleteUnits: row.requiresCompleteUnits,
    quantityPerUnit: row.quantity,
    freebieName: row.freebie.product.name,
    freebieImage: row.freebie.imageMedium,
  };
};
